import {makeAutoObservable, reaction, runInAction} from "mobx";
import type {m_taxonomy} from "../services/classes/TaxonomyClasses";
import type {AxiosResponse} from "axios";
import {isAxiosError} from "axios";
import type {Observable, Subscription} from "rxjs";
import {catchError, EMPTY, forkJoin, from, mergeMap, of, tap} from "rxjs";
import type {BagStore} from "./BagStore";
import type AuthStore from "../authentication/AuthStore";
import type ProfileStore from "./ProfileStore";
import type {MithraMaterializedApi} from "../services/MithraMaterializedApi";
import type {BagLoadingStore} from "./BagLoadingStore";
import {AxoisRequestManager} from "./managers/RequestManager";
import type TaxonomyEditorStore from "./TaxonomyEditorStore";
import {environment} from "../env";
import {ApprovalStatusEnum} from "../services/classes/AiClasses";
import {isUnderConstructionMode} from "../utils/underConstructionMode.ts";

export type HistoryState = 'updating_history'
    | 'applying_action'
    | 'ready'

/**
 * For controlling the taxonomy
 */
export default class TaxonomyManagerStore {
    private getSubscription?: Subscription;

    /**
     * A specific taxonomy that is required, not related to the databag
     */
    desiredTaxonomyId?: number | null;

    taxonomyApiName = '';
    taxonomy: m_taxonomy.FullSerializer | undefined | null //undefied means it's loading, null means it's not found
    error: string = '';

    historyState: HistoryState = 'ready';
    history: m_taxonomy.SimpleTaxonomyOperationSerializer[] | undefined;

    allTaxonomyList: m_taxonomy.SimpleSerializer[] | undefined = undefined

    taxonomyEditorStore: TaxonomyEditorStore = undefined as any as TaxonomyEditorStore;

    constructor(
        private api: MithraMaterializedApi,
        private bagStore: BagStore,
        private bagLoadingStore: BagLoadingStore,
        private authStore: AuthStore,
        private profileStore: ProfileStore,
    ) {
        if (this.profileStore.p.hardcodeTaxonomyId) {
            this.desiredTaxonomyId = this.profileStore.p.hardcodeTaxonomyId;
        }
        makeAutoObservable(this)
        reaction(() => [bagStore.bagId, this.desiredTaxonomyId, this.bagStore.bag?.taxonomy] as const,
            ([bagId, desiredTaxonomyId, databagTaxonomy]) => {
                console.debug(`TaxonomyManagerStore: Bag or taxonomy changed`, {
                    bagId,
                    desiredTaxonomyId,
                    databagTaxonomy
                })

                //Rest the state
                if (desiredTaxonomyId) {
                    this.requestTaxonomy(desiredTaxonomyId)
                } else if (bagId && databagTaxonomy) {
                    this.requestTaxonomy(databagTaxonomy)
                } else {
                    this._setTaxonomy(null)
                    this.history = undefined
                }
            })
    }

    setDesiredTaxonomyId(taxonomyId?: number | null) {
        this.desiredTaxonomyId = taxonomyId;
    }

    requestTaxonomyList() {
        this.api.listMTaxonomy()
            .then(r => {
                    this.allTaxonomyList = r.data;
                }
            )
    }

    //this might need to be inverted
    setTaxonomyInDatabag(taxonomyId: number) {
        this.api.setDatabagForTaxonomy(this.bagStore.bagId, {taxonomy: taxonomyId}).then(() =>
            this.bagLoadingStore.loadBag(this.bagStore.bagId)
        );
    }

    requestTaxonomy(taxonomyId: number) {
        if (this.getSubscription) this.getSubscription.unsubscribe()
        this.taxonomy = undefined;
        this.taxonomyApiName = '';

        const getTaxonomyId: Observable<number> = of(taxonomyId)
        this.getSubscription = getTaxonomyId
            .pipe(
                mergeMap(taxonomyId => forkJoin([
                    from(this.api.getTaxonomy(taxonomyId)).pipe(tap(
                        r => this._setTaxonomy(r.data)
                    )),
                    from(this.api.listMTaxonomyHistory(taxonomyId)).pipe(tap(
                        r => runInAction(() => this.history = r.data)
                    )),
                ])),
                catchError(err => {
                    // console.error(err, {bag: bagId, taxonomy: taxonomyId})
                    runInAction(() => {
                        this._setTaxonomy(undefined)
                        this.history = undefined
                        this.error = String(err)
                    })
                    return EMPTY
                }),
            )
            .subscribe({
                next: () => this.setError(''),
            })
    }

    _setTaxonomy(taxonomy: m_taxonomy.FullSerializer | undefined | null) {
        this.taxonomy = taxonomy;
        this.taxonomyApiName = taxonomy?.name || '';
    }

    get minHistoryNumber() {
        if (!this.history) return -1
        return Math.min(...this.history.map(h => h.operation_number))
    }

    get maxHistoryNumber() {
        if (!this.history) return -1
        return Math.max(...this.history.map(h => h.operation_number))
    }

    get stateInSync(): boolean {
        return this.historyState === 'ready';
    }

    get incompleteCategories(): string[][] {
        if (!this.profileStore.p.allowIncompleteTaxonomy && this.taxonomy) {
            return TaxonomyManagerStore.getIncompleteCategories([this.taxonomy.diff_tree], this.taxonomy.size)
                .map(path => path.slice(1).map(t => t.label))
                .sort()
        }
        return [];
    }

    private static getIncompleteCategories(path: m_taxonomy.Tree[], taxonomySize: number): m_taxonomy.Tree[][] {
        const incompleteNodes: m_taxonomy.Tree[][] = []
        const level = path.length - 1;
        if (level <= taxonomySize - 1) {
            const tree = path[path.length - 1];
            if (tree.children.length === 0) {
                return [path];
            }
            tree.children.forEach(c => {
                incompleteNodes.push(...TaxonomyManagerStore.getIncompleteCategories([...path, c], taxonomySize))
            })
        }
        return incompleteNodes;
    }

    /**
     * Shows if the user can undo
     * Also it shows if there exists a change to actually undo
     */
    get undoAllowed() {
        return this.hasChange;
    }

    get hasChange() {
        if (this.historyState !== 'ready') return false
        if (this.canEditTaxonomy && this.taxonomy && this.history !== undefined) {
            return this.taxonomy.current_operation_number > this.minHistoryNumber
        }
        return false
    }

    get redoAllowed() {
        if (this.historyState !== 'ready') return false
        if (this.canEditTaxonomy && this.taxonomy && this.history !== undefined) {
            return this.taxonomy.current_operation_number < this.maxHistoryNumber
        }
        return false
    }

    /**
     * Get a list of operations that have happened before, including the state before the operation (to revert it)
     */
    get undoHistory(): {operation: m_taxonomy.SimpleTaxonomyOperationSerializer, undoNumber: number}[] {
        if (!this.history || !this.taxonomy) return []
        const currentOperationNumber = this.taxonomy.current_operation_number;
        const firstAction = this.history[0]
        const undoHistory = this.history
            .filter((operation, index) =>
                // The first operation can never be undone
                // All operations should have happened before, so including the current operation_number
                index >= 1 && operation.operation_number <= currentOperationNumber
            )
            .map((operation, index, array) => {
                // If we want to undo an action, we should goto the state before that actions
                const before = index === 0 ? firstAction : array[index - 1]
                return {
                    operation,
                    undoNumber: before.operation_number,
                }
            })
        // Show the most recent operation as the first
        undoHistory.reverse()
        return undoHistory
    }

    get undoContainsOtherUser() {
        return this.undoHistory.some(({operation}) => operation.author && operation.author.id !== this.authStore.userId)
    }

    get redoContainsOtherUser() {
        return this.redoHistory.some(operation => operation.author && operation.author.id !== this.authStore.userId)
    }

    get redoHistory() {
        if (!this.history || !this.taxonomy) return []
        const currentOperationNumber = this.taxonomy?.current_operation_number || 1;
        return this.history.filter(h => h.operation_number > currentOperationNumber)
    }

    gotoHistoryState(number: number) {
        if (this.historyState !== 'ready') return;
        if (!this.taxonomy) return
        const taxonomyId = this.taxonomy.id;
        this.historyState = 'updating_history'
        this.api.gotoMTaxonomyHistory(taxonomyId, {goto_history_number: number})
            .then(r => runInAction(() => {
                this._setTaxonomy(r.data)
                // redraw the taxonomy
                this.taxonomyEditorStore.redrawTaxonomy(r.data);

                // Retrieve the next history elements
                return this.api.listMTaxonomyHistory(taxonomyId)
                    .then(r => runInAction(() => this.history = r.data))
            }))
            .finally(() => runInAction(() => {
                this.historyState = 'ready'
            }))
    }

    onChangeTaxonomy(operation: m_taxonomy.Operation, newState: m_taxonomy.Tree) {
        if (!this.taxonomy) return
        console.log('Updating taxonomy to API', operation)
        const taxonomyId = this.taxonomy.id;

        const nextOperation: m_taxonomy.CreateTaxonomyOperationSerializer = {
            next_node_id: this.taxonomy.next_node_id + 1,
            operation_number: this.taxonomy.current_operation_number + 1,
            operation_name: operation.valueOf(),
            state: newState,
        };
        const promise: Promise<AxiosResponse<m_taxonomy.FullSerializer>> = this.api.storeTaxonomyState(taxonomyId, nextOperation)

        this.historyState = 'applying_action'

        return promise
            .then(r => {
                this.error = '';
                const taxonomy = r.data;
                runInAction(() => {
                    this._setTaxonomy(taxonomy)
                    this.history = this.history?.filter(h => h.operation_number < taxonomy.current_operation_number)
                })
                return this.api.listMTaxonomyHistory(taxonomyId)
                    .then(r => runInAction(() => this.history = r.data))
            })
            .catch(err => {
                console.error(err)
                if (isAxiosError(err) && err.response) {
                    if (err.response.data.message) {
                        this.setError('Cannot change the taxonomy. ' + err.response.data.message)
                        return;
                    } else if (err.response.data.operation_number) {
                        this.setError('Cannot change the taxonomy due to a synchronization issue. Please refresh the page.')
                        return;
                    }
                }
                this.setError('Cannot change the taxonomy')
            })
            .finally(() => runInAction(() => {
                this.historyState = 'ready'
            }))
    }

    get canEditTaxonomy() {
        if (isUnderConstructionMode(this.authStore)) return false;
        if (!this.taxonomy) return false;
        if (this.authStore.isMithraStaffGroup) return true;
        if (this.profileStore.p.taxonomyBuilderViewOnly) return false;

        if (this.taxonomy.active_approval_request) {
            switch (this.taxonomy.active_approval_request.current_status.status) {
                case ApprovalStatusEnum.APPROVED:
                case ApprovalStatusEnum.REJECTED:
                    // Continue editing
                    return true;
                default:
                    // Wait for the system or Mithra staff to complete
                    return false;
            }
        }
        return true;
    }

    /**
     * Whether the user can send the taxonomy for approval
     */
    get canGotToApprovalErrorMessage(): '' | string {
        if (environment.package === 'sales_demo') return ''; // Sales demo always allows a send for approval

        if (!this.canEditTaxonomy) return 'Cannot edit the taxonomy';
        if (!this.stateInSync) return 'Loading ...'
        const activeApprovalRequest = this.taxonomy?.active_approval_request;
        if (activeApprovalRequest) {
            if (!ApprovalStatusEnum.isConcluded(activeApprovalRequest.current_status.status)) {
                return 'Please resolve the current approval request before sending another one';
            }
        }
        if (!this.hasChange) return 'No changes detected'

        return ''; // Ok
    }

    overwriteTaxonomyHealthCheckResult(has_taxonomy_health_check: boolean) {
        if (this.taxonomy) {
            // It must become true the next time we query the backend
            this.taxonomy.has_taxonomy_health_check = has_taxonomy_health_check;
        }
    }

    readonly _updateTaxonomyRequest = new AxoisRequestManager<{
        taxonomyId: number, data: Partial<m_taxonomy.UpdateSerializer>
    }, m_taxonomy.UpdateSerializer>(({taxonomyId, data}) =>
        from(this.api.updateTaxonomy(taxonomyId, data)).pipe(
            tap(resp => runInAction(() => {
                if (!this.taxonomy) return;
                this.taxonomyApiName = resp.data.name;
                this.taxonomy.name = resp.data.name;
                this.taxonomy.size = resp.data.size;
            }))
        )
    )

    setTaxonomyName(name: string) {
        if (this.taxonomy) {
            this.taxonomy.name = name;
        }
    }

    storeTaxonomyName() {
        if (!this.taxonomy) return;
        const name = this.taxonomy.name;
        if (name !== this.taxonomyApiName) {
            this._updateTaxonomyRequest.request({taxonomyId: this.taxonomy.id, data: {name}})
        }
    }

    setError(error: string) {
        this.error = error;
    }

    updateTaxonomySize(taxonomySize: number) {
        if (!this.taxonomy?.id) return;
        this._updateTaxonomyRequest.request({taxonomyId: this.taxonomy.id, data: {size: taxonomySize}})
    }
}
